import React, { useState } from 'react';
import { ArrowRight, ExternalLink, Play } from 'lucide-react';
import { buildYouTubeTimestampUrl } from '../data/productVideoCore.js';

const ProductVideoFeature = ({
  video,
  title,
  highlights = [],
  checkpoints = [],
  actions = [],
  watchLabel = 'Watch on YouTube',
  highlightPlacement = 'sidebar',
  checkpointLimit
}) => {
  const [isPlayerLoaded, setIsPlayerLoaded] = useState(false);

  if (!video?.embedUrl) return null;

  const visibleCheckpoints = Number.isFinite(checkpointLimit)
    ? checkpoints.slice(0, Math.max(0, checkpointLimit))
    : checkpoints;
  const showHighlightsInPlayer = highlightPlacement === 'player';
  const layoutClassName = `product-video-layout${showHighlightsInPlayer ? ' product-video-layout--integrated' : ''}`;
  const videoTitle = title || video.youtubeTitle;
  const playerUrl = `${video.embedUrl}${video.embedUrl.includes('?') ? '&' : '?'}autoplay=1`;
  const posterUrl = video.posterUrl || video.thumbnailUrl || video.fallbackThumbnailUrl;

  return (
    <div className={layoutClassName}>
      <div className="product-video-player">
        <div className="product-video-frame">
          {isPlayerLoaded ? (
            <iframe
              src={playerUrl}
              title={videoTitle}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <a
              className="product-video-facade"
              href={video.watchUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${videoTitle}: ${watchLabel}`}
              onClick={(event) => {
                if (
                  event.button !== 0
                  || event.metaKey
                  || event.ctrlKey
                  || event.shiftKey
                  || event.altKey
                ) {
                  return;
                }
                event.preventDefault();
                setIsPlayerLoaded(true);
              }}
            >
              {posterUrl ? (
                <img
                  src={posterUrl}
                  srcSet={video.posterSrcSet}
                  sizes={video.posterSrcSet ? '(max-width: 720px) calc(100vw - 48px), 34vw' : undefined}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  width="640"
                  height="360"
                />
              ) : null}
              <span className="product-video-facade-play" aria-hidden="true">
                <Play size={30} fill="currentColor" />
              </span>
              <span className="product-video-facade-label">{watchLabel}</span>
            </a>
          )}
        </div>
        <div className="product-video-player-meta">
          <span className="product-video-player-channel">
            <Play size={14} />
            {video.channelName}
          </span>
          <a
            className="product-video-player-link"
            href={video.watchUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {watchLabel}
            <ExternalLink size={14} />
          </a>
        </div>

        {showHighlightsInPlayer && highlights.length ? (
          <div className="product-video-player-highlights">
            {highlights.map((item) => (
              <article key={item.title} className="product-video-highlight-card">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        ) : null}
      </div>

      <aside className="product-video-sidebar">
        {!showHighlightsInPlayer && highlights.length ? (
          <div className="product-video-highlight-grid">
            {highlights.map((item) => (
              <article key={item.title} className="product-video-highlight-card">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        ) : null}

        {visibleCheckpoints.length ? (
          <div className="product-video-checkpoints">
            {visibleCheckpoints.map((item) => (
              <a
                key={`${item.timeLabel}-${item.title}`}
                className="product-video-checkpoint"
                href={buildYouTubeTimestampUrl(video.watchUrl, item.startSeconds)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="product-video-checkpoint-time">{item.timeLabel}</span>
                <div className="product-video-checkpoint-copy">
                  <strong>{item.title}</strong>
                  <p>{item.body}</p>
                </div>
              </a>
            ))}
          </div>
        ) : null}

        {actions.length ? (
          <div className="product-video-actions">
            {actions.map((action) => (
              <a
                key={`${action.href}-${action.label}`}
                className={`product-video-action ${action.variant === 'secondary' ? 'secondary' : 'primary'}`}
                href={action.href}
                {...(action.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {action.label}
                {action.external ? <ExternalLink size={14} /> : <ArrowRight size={14} />}
              </a>
            ))}
          </div>
        ) : null}
      </aside>
    </div>
  );
};

export default ProductVideoFeature;
